import { useState, useEffect } from 'react';
import { Box, styled } from '@mui/material';
import { UseFormReturn } from 'react-hook-form';
import { DateTime } from 'luxon';
import {
  DateRangeOutlined as DateRangeOutlinedIcon,
  ScheduleOutlined as ScheduleOutlinedIcon,
  LocationOnOutlined as LocationOnOutlinedIcon,
  PersonOutlineOutlined as PersonOutlineOutlinedIcon,
  DriveEtaOutlined as DriveEtaOutlinedIcon,
} from '@mui/icons-material';

import { useAppSelector } from '@/hooks/useAppSelector';
import { useCommonTranslation } from '@/hooks/useCommonTranslation';
import { useAlternateContactType } from '@/hooks/display/useAlternateContactType';

import { selectPassengerClientData } from '@/data/client.slice';
import * as lookupSlice from '@/data/lookup.slice';
import { selectNarrativeMetadata } from '@/data/reservation.slice';
import { selectProviderList } from '@/data/provider.slice';

import { TripLegType } from '@/data/models/reservation.model';
import { Passenger } from '@/data/models/passenger.model';
import {
  ReservationFormInput,
  ReservationFormValues,
} from './schema/reservationForm.schema';

import dateUtils from '@/services/DateUtils';
import { phoneUtils } from '@/services/phoneUtils';

const PREFIX = 'ReservationSummaryDetails';

const classes = {
  row: `${PREFIX}-row`,
  topSpace: `${PREFIX}-topSpace`,
  iconContainer: `${PREFIX}-iconContainer`,
  textContainer: `${PREFIX}-textContainer`,
  noteContainer: `${PREFIX}-noteContainer`,
  note: `${PREFIX}-note`,
  bgdots: `${PREFIX}-bgdots`,
  padBottom: `${PREFIX}-padBottom`,
  marginBottom: `${PREFIX}-marginBottom`,
  strong: `${PREFIX}-strong`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`& .${classes.row}`]: {
    display: 'flex',
  },

  [`& .${classes.topSpace}`]: {
    marginTop: theme.spacing(2),
  },

  [`& .${classes.iconContainer}`]: {
    display: 'inline-block',
    width: 24,
    textAlign: 'center',
    marginRight: theme.spacing(1.5),
  },

  [`& .${classes.bgdots}`]: {
    height: 28,
    backgroundImage: `url('/1dot.png')`,
    backgroundRepeat: 'repeat-y',
    backgroundPositionX: 'center',
  },

  [`& .${classes.padBottom}`]: {
    paddingBottom: theme.spacing(1),
  },

  [`& .${classes.marginBottom}`]: {
    marginBottom: theme.spacing(1),
  },

  [`& .${classes.textContainer}`]: {
    display: 'inline-block',
    lineHeight: '150%',
    letterSpacing: 0.15,
  },

  [`& .${classes.noteContainer}`]: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(0.5),
    flexWrap: 'wrap',
  },

  [`& .${classes.note}`]: {
    height: 24,
    paddingLeft: theme.spacing(0.5),
    paddingRight: theme.spacing(0.5),
    borderRadius: theme.spacing(0.5),
    backgroundColor: 'rgba(25, 124, 179, 0.12)',
  },

  [`& .${classes.strong}`]: {
    fontWeight: 600,
  },
}));

const PassengerDetail = ({
  passenger,
  noteBCs,
  allowText,
}: {
  passenger: Passenger;
  noteBCs: string[];
  allowText: boolean;
}) => {
  const t = useCommonTranslation();
  const mobile = phoneUtils.getPassengerMobilePhone(passenger.passengerPhones);
  const home = phoneUtils.getPassengerHomePhone(passenger.passengerPhones);
  const passengerNoteTypes = useAppSelector(
    lookupSlice.selectPassengerNoteTypes,
  );
  const notes = noteBCs.map((bc) => {
    return (
      passengerNoteTypes.find((nt) => nt.tripLegPassengerNoteTypeBC === bc)
        ?.tripLegPassengerNoteTypeName || ''
    );
  });
  let phone = '';
  if (mobile) {
    phone = phoneUtils.formatPhone(mobile, false, true);
    if (allowText) {
      phone += ` (${t('reservation.receivesTexts')})`;
    }
  } else if (home) {
    phone = phoneUtils.formatPhone(home, false, true);
  }
  return (
    <>
      <Box className={classes.row}>
        <Box className={classes.iconContainer}>
          <PersonOutlineOutlinedIcon color="action" />
        </Box>
        <Box className={`${classes.textContainer} ${classes.strong}`}>
          {`${passenger.firstName} ${passenger.lastName}`}
        </Box>
      </Box>
      {phone && (
        <Box className={classes.row}>
          <Box className={classes.iconContainer} />
          <Box className={classes.textContainer}>{phone}</Box>
        </Box>
      )}
      {!!notes?.length && (
        <Box className={classes.row}>
          <Box className={classes.iconContainer} />
          <Box className={classes.noteContainer}>
            {notes.map((note, index) => {
              return (
                <Box
                  key={`note-${index}`}
                  className={`${classes.textContainer} ${classes.note}`}>
                  {note}
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </>
  );
};

const AdvocateDetail = ({
  type,
  firstName,
  lastName,
  phone,
  sms,
}: {
  type: string;
  firstName: string;
  lastName: string;
  phone: string;
  sms: boolean;
}) => {
  const t = useCommonTranslation();
  const texts = sms ? `(${t('reservation.receivesTexts')})` : '';
  return (
    <>
      <Box className={classes.row}>
        <Box className={classes.iconContainer} />
        <Box
          className={
            classes.textContainer
          }>{`${t('reservation.advocate')} - ${firstName} ${lastName}, ${type}`}</Box>
      </Box>
      <Box className={classes.row}>
        <Box className={classes.iconContainer} />
        <Box
          className={
            classes.textContainer
          }>{`${phoneUtils.formatPhoneString(phone, false, true)} ${texts}`}</Box>
      </Box>
    </>
  );
};

const AttributeDetail = ({
  requestedProvider,
}: {
  requestedProvider?: number;
}) => {
  const t = useCommonTranslation();
  const providers = useAppSelector(selectProviderList);
  const metadata = useAppSelector(selectNarrativeMetadata);

  let providerName = '';
  if (requestedProvider) {
    providerName =
      providers.items.find(
        (p) => p.transportationProviderID === requestedProvider,
      )?.transportationProviderName || '';
  }
  const ambTripMode = metadata.ambulatory
    ? t('reservation.ambulatory')
    : t('reservation.nonAmbulatory');
  const ambCare = metadata.doorToDoor
    ? t('reservation.doorToDoor')
    : metadata.handToHand
      ? t('reservation.handToHand')
      : t('reservation.curbToCurb');
  let vehicleType = metadata.equipmentSize || '';
  vehicleType = vehicleType + ' ' + (metadata.mobilityAid || '');
  vehicleType = (
    vehicleType +
    ' ' +
    (metadata.vehicleEquipmentType || '')
  ).trim();
  if (vehicleType.length > 0) {
    vehicleType = `${vehicleType} ${t('reservation.accessibleVehicle')}`;
  }

  return (
    <>
      {vehicleType && (
        <Box className={`${classes.row} ${classes.topSpace}`}>
          <Box className={classes.iconContainer}>
            <DriveEtaOutlinedIcon color="action" />
          </Box>
          <Box className={`${classes.textContainer} ${classes.strong}`}>
            {vehicleType}
          </Box>
        </Box>
      )}
      <Box className={`${classes.row} ${!vehicleType ? classes.topSpace : ''}`}>
        <Box className={classes.iconContainer}>
          {!vehicleType && <DriveEtaOutlinedIcon color="action" />}
        </Box>
        <Box className={classes.textContainer}>
          <span>{`${t('reservation.tripMode')}:`}&nbsp;</span>
          <span className={classes.strong}>{`${ambTripMode}, ${ambCare}`}</span>
        </Box>
      </Box>
      {!!providerName && (
        <Box className={classes.row}>
          <Box className={classes.iconContainer} />
          <Box className={classes.textContainer}>
            <span>{`${t('reservation.requestedProvider')}:`}&nbsp;</span>
            <span className={classes.strong}>{providerName}</span>
          </Box>
        </Box>
      )}
    </>
  );
};

const TripDetail = ({ tripLeg }: { tripLeg: ReservationSummaryDetailsProps }) => {
  const t = useCommonTranslation();

  const {
    pickupAddressStruct,
    dropoffAddressStruct,
    pickupPhone,
    pickupNotes,
    dropoffPhone,
    dropoffNotes,
    isWillCall,
    appointmentDate,
    tripType,
    roundTripWillCall,
    roundTripReturnDateTime,
    reasonForTrip,
    pickupDateTime,
  } = tripLeg as ReservationSummaryDetailsProps;

  const [purposeCode, setPurposeCode] = useState('');

  const passengerClientData = useAppSelector(selectPassengerClientData);
  
  useEffect(() => {
    if (passengerClientData?.purposeCodes) {
      const purpose = passengerClientData.purposeCodes.find(
        (pc) => pc.purposeCodeUUID === reasonForTrip,
      );
      setPurposeCode(purpose?.name || '');
    }
  }, [passengerClientData, reasonForTrip]);

  const formattedPickupTime = dateUtils.formatTime(pickupDateTime);
  const formattedAppointmentDate = isWillCall
    ? DateTime.fromISO(appointmentDate).toFormat('L/d/yyyy')
    : DateTime.fromISO(appointmentDate).toFormat('L/d/yyyy - h:mm a') || '';
  const isRoundTrip = tripType === TripLegType.RoundTrip;
  const returnPickup = roundTripWillCall 
    ? t('reservation.willCallTime')
    : dateUtils.formatTime(roundTripReturnDateTime);

  const parsedPickupAddress = JSON.parse(pickupAddressStruct);
  const parsedDropoffAddress = JSON.parse(dropoffAddressStruct);

  return (
    <>
      <Box className={`${classes.row} ${classes.topSpace}`}>
        <Box className={classes.iconContainer}>
          <DateRangeOutlinedIcon color="action" />
        </Box>
        <Box className={`${classes.textContainer} ${classes.strong}`}>
          {formattedAppointmentDate}
        </Box>
      </Box>
      <Box className={classes.row}>
        <Box className={classes.iconContainer} />
        <Box className={classes.textContainer}>{purposeCode}</Box>
      </Box>
      <Box className={`${classes.row} ${classes.topSpace}`}>
        <Box className={classes.iconContainer}>
          <ScheduleOutlinedIcon color="action" />
        </Box>
        <Box className={`${classes.textContainer} ${classes.strong}`}>
          {isWillCall
            ? t('reservation.willCall')
            : t('reservation.estimatedPickup', { time: formattedPickupTime })}
        </Box>
      </Box>{' '}
      <Box className={`${classes.row} ${classes.topSpace}`}>
        <Box className={classes.iconContainer}>
          <LocationOnOutlinedIcon color="primary" />
        </Box>
        <Box className={`${classes.textContainer} ${classes.strong}`}>
          {`${parsedPickupAddress?.addressLine1}, ${parsedPickupAddress?.city}, ${parsedPickupAddress?.stateCode}, ${parsedPickupAddress?.zipCode}`}
        </Box>
      </Box>
      {pickupPhone && (
        <Box className={classes.row}>
          <Box
            className={`${classes.iconContainer} ${classes.bgdots} ${!pickupNotes ? classes.padBottom : ''}`}
          />
          <Box className={classes.textContainer}>
            {phoneUtils.formatPhoneString(pickupPhone)}
          </Box>
        </Box>
      )}
      {pickupNotes && (
        <Box className={classes.row}>
          <Box
            className={`${classes.iconContainer} ${classes.bgdots} ${classes.padBottom}`}
          />
          <Box
            className={`${classes.textContainer} ${classes.note} ${classes.marginBottom}`}>
            {pickupNotes}
          </Box>
        </Box>
      )}
      <Box className={classes.row}>
        <Box className={classes.iconContainer}>
          <LocationOnOutlinedIcon color="error" />
        </Box>
        <Box className={`${classes.textContainer} ${classes.strong}`}>
          {`${parsedDropoffAddress?.addressLine1}, ${parsedDropoffAddress?.city}, ${parsedDropoffAddress?.stateCode}, ${parsedDropoffAddress?.zipCode}`}
        </Box>
      </Box>
      {dropoffPhone && (
        <Box className={classes.row}>
          <Box
            className={`${classes.iconContainer} ${isRoundTrip ? classes.bgdots : ''}`}
          />
          <Box className={classes.textContainer}>
            {phoneUtils.formatPhoneString(dropoffPhone)}
          </Box>
        </Box>
      )}
      <Box className={`${classes.row} ${classes.padBottom}`}>
        {dropoffNotes && (
          <>
            <Box
              className={`${classes.iconContainer} ${isRoundTrip ? classes.bgdots : ''} ${isRoundTrip && dropoffNotes ? classes.padBottom : ''}`}
            />

            <Box
              className={`${classes.textContainer} ${classes.note} ${classes.marginBottom}`}>
              {dropoffNotes}
            </Box>
          </>
        )}
      </Box>
      {isRoundTrip && (
        <>
          <Box className={classes.row}>
            <Box className={classes.iconContainer}>
              <LocationOnOutlinedIcon color="error" />
            </Box>
            <Box className={`${classes.textContainer} ${classes.strong}`}>
              {`${parsedPickupAddress?.addressLine1}, ${parsedPickupAddress?.city}, ${parsedPickupAddress?.stateCode}, ${parsedPickupAddress?.zipCode}`}
            </Box>
          </Box>
          <Box className={classes.row}>
            <Box className={classes.iconContainer} />
            <Box className={classes.textContainer}>{returnPickup}</Box>
          </Box>
        </>
      )}
    </>
  );
};

export type ReservationSummaryDetailsProps = {
  reasonForTrip: string;
  pickupAddressStruct: string;
  dropoffAddressStruct: string;
  pickupPhone: string;
  pickupNotes: string;
  dropoffPhone: string;
  dropoffNotes: string;
  isWillCall: boolean;
  appointmentDate: string;
  tripType: TripLegType;
  roundTripWillCall: boolean;
  roundTripReturnDateTime: DateTime | undefined;
  pickupDateTime: DateTime;
}

const ReservationSummaryDetails = ({
  passenger,
  tripLegs,
  form,
}: {
  passenger: Passenger;
  tripLegs: ReservationSummaryDetailsProps[];
  form: UseFormReturn<ReservationFormInput, unknown, ReservationFormValues>;
}) => {
  const alternateContact = form.watch('AlternateContact');
  const advocateDetailType = useAlternateContactType(form.watch('AlternateContactType'));

  return (
    <StyledBox>
      <PassengerDetail
        passenger={passenger}
        noteBCs={form.watch('PassengerNoteTypes') ?? []}
        allowText={form.watch('AllowText') ?? false}
      />
      {alternateContact && (
        <AdvocateDetail
          type={advocateDetailType}
          firstName={form.watch('AlternateContactFirstName') as string}
          lastName={form.watch('AlternateContactLastName') as string}
          phone={form.watch('AlternateContactPhone') as string}
          sms={form.watch('AlternateContactAllowSMS') as boolean}
        />
      )}
      {tripLegs.map((tripLeg) => (
        <TripDetail key={tripLeg.reasonForTrip} tripLeg={tripLeg} />
      ))}
      <AttributeDetail
        requestedProvider={form.watch('RequestedTransportationProviderID') ?? undefined}
      />
    </StyledBox>
  );
};

export { ReservationSummaryDetails };
